<h1 class="h5">Access to Opt-Out Databases</h1>

<ul class="nav nav-tabs mb-3" id="tabs" role="tablist">
  <li class="nav-item" role="presentation">
    <a (click)="tab = 'overview'" [class.active]="tab === 'overview'" class="nav-link" role="button">Overview</a>
  </li>
  <li class="nav-item" role="presentation">
    <a (click)="tab = 'lookup'" [class.active]="tab === 'lookup'" class="nav-link" role="button">Email Lookup Form</a>
  </li>
  <li class="nav-item" role="presentation">
    <a (click)="tab = 'doc'" [class.active]="tab === 'doc'" class="nav-link" role="button">API Doc</a>
  </li>
</ul>

@switch (tab) {
  @case ('overview') {
    <div class="card shadow border-0">
      <div class="card-body">
        <h2>Overview</h2>
        <p><i class="bi bi-info-circle-fill me-2 text-secondary"></i>To ensure respect for users' choices and the compliance of email
          campaigns, we provide our partners with a centralized opt-out management system.</p>
        <p>This system allows for the reliable and secure identification of contacts who have expressed their wish to no
          longer receive advertising communications related to our offers.</p>

        <h3 class="mt-5">How it Works</h3>
        <p>We provide the following resources:</p>
        <ul>
          <li>📄 An email lookup form allowing to check any email</li>
          <li>🔗 An API allowing real-time querying of the opt-out database</li>
          <li>📝 A link to the public opt-out form, accessible to users</li>
        </ul>
        <p>These different methods can be used according to your technical constraints and internal processes.</p>

        <h3 class="mt-5">Obligations for Email Partners</h3>
        <p>As a partner, you agree to:</p>
        <ul>
          <li>❌ Systematically exclude from your mailings any address appearing in the opt-out databases</li>
          <li>🔄 Regularly consult the API or the provided form to ensure up-to-date data</li>
          <li>🔐 Use the data solely for managing unsubscribes, without attempting to re-identify users</li>
          <li>⚖️ Comply with all applicable legal and regulatory obligations regarding electronic marketing</li>
        </ul>
        <p>Adherence to these rules is essential to ensure a respectful user experience and maintain the quality of our
          partnerships.</p>
      </div>
    </div>
  }

  @case ('lookup') {
    <div class="card shadow border-0">
      <div class="card-body">
        <h2>Email Opt-Out Lookup</h2>
        <p class="text-muted">Verify which products this email has opted out of receiving promotional emails.</p>

        <form (submit)="submit()" [formGroup]="form">
          <div class="row">
            <div class="col-auto">
              <label for="email" class="form-label small fw-bold" style="min-width: 300px;">Email</label>
              <input formControlName="email" type="email" class="form-control form-control-sm" id="email">
            </div>

            <div class="col-auto">
              <label> </label><br>
              <button [disabled]="form.invalid" class="btn btn-sm btn-primary fw-bold" type="submit">CHECK EMAIL</button>
            </div>
          </div>
        </form>

        @if (products !== null) {
          <div class="card shadow border-0 mt-3">
            <div class="card-body">
              @if (products.length) {
                <p>⚠️ This lead has opted out of the products listed below. Do not send promotional emails for these
                  products.</p>
                <div class="table-responsive">
                  <table class="table table-hover table-striped">
                    <thead class="table-light">
                    <tr>
                      <th>Email</th>
                      <th>Product ID</th>
                      <th>Product name</th>
                      <th>Product identifier</th>
                    </tr>
                    </thead>
                    <tbody>
                      @for (product of products; track product.id) {
                        <tr>
                          <td class="align-middle">{{ currentEmail }}</td>
                          <td class="align-middle">{{ product.id }}</td>
                          <td class="align-middle">{{ product.name }}</td>
                          <td class="align-middle">{{ product.identifier }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              } @else {
                <i class="bi bi-check-circle-fill text-success me-2"></i>This lead has not opted out of any products.
              }
            </div>
          </div>
        }
      </div>
    </div>
  }

  @case ('doc') {
    <div class="card shadow border-0">
      <div class="card-body">
        <h2>Optout API</h2>

        <p>The API is a REST JSON API.</p>

        <h3>Endpoint</h3>
        <div class="code-block">https://api.oktopus.world/api/tools</div>

        <h3 class="mt-4">Authentification</h3>
        <p>Bearer Token</p>
        <p><i class="bi bi-info-circle-fill text-info-emphasis me-2"></i>The token is generated from the backend. If you don’t already have one, please ask your account manager.</p>

        <h3 class="my-4">Optout Request</h3>

        <b><span class="request-method">POST</span> /lead/optout</b>
        <p>This query allows you to access databases to determine the status of a lead at a given time across all brands. It is primarily used in marketing strategies that aim to direct leads to brands where they can register (or be reactivated).</p>

        <h4 class="mt-4">JSON body content</h4>
        <div class="table-responsive">
          <table class="table table-bordered w-auto">
            <thead class="table-secondary">
            <tr>
              <th>JSON field</th>
              <th>Mandatory</th>
              <th>Example</th>
              <th>Description</th>
            </tr>
            </thead>
            <tbody>
            <tr>
              <td>email</td>
              <td>Yes</td>
              <td>{{ 'test@gmail.com' }}</td>
              <td>E-mail address</td>
            </tr>
            </tbody>
          </table>
        </div>

        <h4 class="mt-4">Response</h4>
        <b><span class="request-method">200</span> OK</b>
        <p>Returns the list of products for which the lead no longer wants to receive promotional emails.</p>
        <pre class="code-block">{{ jsonBody | json }}</pre>

        <h4 class="mt-4">Example request</h4>
        <div class="code-block">
          POST /api/tools/lead/optout HTTP/1.1<br/>
          Host: api.oktopus.world<br/>
          Authorization: Bearer zQ16XGEytcs254WJos3sTRPnTQoAsvuN<br/>
          Accept: */*<br/>
        </div>
      </div>
    </div>
  }
}
